'use client';

/* eslint-disable @next/next/no-img-element */
import { useEffect, useRef, useState } from 'react';
import { Loader2, Play } from 'lucide-react';

import { resolveVideoEmbed } from '@kit/shared/video-embed';
import { withPosterFragment } from '@kit/shared/video-poster';
import { Trans } from '@kit/ui/trans';

import { resolveVideoSource } from '~/lib/media/video-source';
import { getYouTubeThumbnailUrlFallbacks } from '~/lib/media/youtube';
import { MuxHlsPlayer } from './mux-hls-player';

export interface VideoTileProps {
  /** External host link (YouTube / Vimeo / TikTok / …). */
  externalUrl?: string | null;
  /** Playable URL for an uploaded clip: either a direct http(s) URL (signed or
   * public storage URL) OR a same-origin absolute proxy path such as
   * `/api/media/cover/original/<base64>` (302s to a fresh signed video object).
   * A raw storage_path (no scheme, no leading slash — loader couldn't resolve a
   * URL) is ignored: feeding it to `<video src>` raises NotSupportedError. */
  uploadedUrl?: string | null;
  /** Cached oEmbed thumbnail (Vimeo / TikTok). Falls back to the YouTube
   * hqdefault derived from `externalUrl`, then the uploaded clip's first
   * frame, then the brand-tinted play marker. */
  thumbnailUrl?: string | null;
  /** Visible title on the gradient scrim. Omitted from render when empty
   * (a "Untitled clip" placeholder reads as unfinished on a portfolio);
   * a11y labels always get a non-empty value. */
  title?: string | null;
  /** artist_media row id — required to fetch a signed Mux token (BAL-721). */
  mediaId?: string | null;
  /** Mux playback id (present once the encode is ready). Drives HLS playback. */
  muxPlaybackId?: string | null;
  /** Mux encode lifecycle: 'preparing' | 'ready' | 'errored' | null (R4/R3). */
  muxStatus?: string | null;
}

/**
 * Canonical editorial video tile — 16:9 click-to-play card with uppercase
 * source eyebrow, serif title, and centered play overlay. Extracted from
 * the public dancer view so every surface (dancer profile, org page,
 * choreography, client media) embeds video the same way.
 *
 * Playback: clicking the tile plays the clip inline (privacy-friendly
 * YouTube / Vimeo iframe, or native <video> for uploaded clips) — it does
 * NOT open a new tab. Embeds load only on click, so pages stay fast.
 * Unknown hosts (e.g. TikTok, which CSP can't embed) fall back to a
 * new-tab link. Both embed hosts are allowlisted in CSP frame-src /
 * media-src (see lib/security-headers.mjs).
 */
export function VideoTile({
  externalUrl,
  uploadedUrl,
  thumbnailUrl,
  title,
  mediaId,
  muxPlaybackId,
  muxStatus,
}: VideoTileProps) {
  const [playing, setPlaying] = useState(false);

  const fetchableUploadedUrl = isPlayableUploadedUrl(uploadedUrl)
    ? uploadedUrl
    : null;
  const external = externalUrl ?? null;
  const source = sourceOf(external);
  const visibleTitle = title?.trim() || null;
  const a11yTitle = visibleTitle ?? 'video';

  const cachedThumb = isHttpUrl(thumbnailUrl) ? thumbnailUrl : null;
  const youTubeFallbacks = cachedThumb
    ? []
    : getYouTubeThumbnailUrlFallbacks(external);
  const tileImage = cachedThumb ?? youTubeFallbacks[0] ?? null;
  const showUploadedPoster = !tileImage && !external && !!fetchableUploadedUrl;

  const embed = getEmbedUrl(external, fetchableUploadedUrl);

  // BAL-721: decide the playback pipeline. A ready Mux asset plays browser-safe
  // HLS; a still-encoding one shows a processing state; otherwise fall back to
  // the native element / external embed below (R3/R4).
  const renderSource = resolveVideoSource({
    externalUrl: external,
    uploadedUrl: fetchableUploadedUrl,
    muxPlaybackId,
    muxStatus,
  });
  const isMux = renderSource.kind === 'mux' && !!mediaId;

  // Still encoding — never show a broken tile (R4).
  if (renderSource.kind === 'processing') {
    return (
      <div className={TILE_CLASS} aria-label={`${a11yTitle} — processing`}>
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent"
        />
        <div className="absolute inset-0 grid place-items-center">
          <span className="ring-foreground/20 grid size-14 place-items-center rounded-md bg-white/10 text-white ring-1 backdrop-blur-sm">
            <Loader2 className="size-6 animate-spin" />
          </span>
        </div>
        <div className="absolute right-4 bottom-4 left-4 text-white">
          <p className="font-mono text-[10px] tracking-[0.22em] uppercase opacity-80">
            <Trans i18nKey="common:processing" />
          </p>
          {visibleTitle && (
            <p className="font-heading mt-1 text-lg leading-tight tracking-[-0.01em] sm:text-xl">
              {visibleTitle}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Playing a ready Mux asset — signed HLS fills the tile in place.
  if (playing && isMux) {
    return (
      <div className="border-foreground/15 bg-secondary relative aspect-video overflow-hidden rounded-md border">
        <MuxHlsPlayer
          mediaId={mediaId!}
          className="absolute inset-0 h-full w-full bg-black object-contain"
          controls
          autoPlay
          playsInline
          aria-label={a11yTitle}
        />
      </div>
    );
  }

  // Playing — the embedded player fills the tile in place. No new tab.
  if (playing && embed) {
    return (
      <div className="border-foreground/15 bg-secondary relative aspect-video overflow-hidden rounded-md border">
        {embed.kind === 'iframe' ? (
          <iframe
            src={embed.url}
            title={a11yTitle}
            className="absolute inset-0 h-full w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <InlineUploadedVideo url={embed.url} title={a11yTitle} />
        )}
      </div>
    );
  }

  const overlay = (
    <>
      {tileImage && (
        <img
          src={tileImage}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
          decoding="async"
          onError={(e) => {
            const img = e.currentTarget;
            const next = youTubeFallbacks.indexOf(img.src) + 1;
            if (next > 0 && next < youTubeFallbacks.length) {
              img.src = youTubeFallbacks[next]!;
            } else {
              img.style.display = 'none'; // coral bg-secondary shows through
            }
          }}
        />
      )}
      {showUploadedPoster && (
        <video
          src={withPosterFragment(fetchableUploadedUrl)}
          aria-hidden="true"
          muted
          playsInline
          preload="metadata"
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent"
      />
      <div className="absolute inset-0 grid place-items-center">
        <span className="ring-foreground/20 grid size-14 place-items-center rounded-md bg-white/10 text-white ring-1 backdrop-blur-sm transition-colors group-hover:bg-white/20">
          <Play className="ml-0.5 size-6 fill-current" />
        </span>
      </div>
      <div className="absolute right-4 bottom-4 left-4 text-white">
        {source && (
          <p className="font-mono text-[10px] tracking-[0.22em] uppercase opacity-80">
            {source}
          </p>
        )}
        {visibleTitle && (
          <p className="font-heading mt-1 text-lg leading-tight tracking-[-0.01em] sm:text-xl">
            {visibleTitle}
          </p>
        )}
      </div>
    </>
  );

  // Embeddable clip (Mux HLS or inline embed) — click plays it in place.
  if (embed || isMux) {
    return (
      // eslint-disable-next-line kit-components/use-kit-primitives -- editorial tile sizes via aspect-video/w-full with absolutely-positioned overlay children; kit Button's forced h-9 size overrides the aspect-ratio height and collapses the tile
      <button
        type="button"
        onClick={() => setPlaying(true)}
        aria-label={`Play ${a11yTitle}`}
        className={TILE_CLASS}
      >
        {overlay}
      </button>
    );
  }

  // Not embeddable (unknown host) — open the source in a new tab.
  const href = external ?? fetchableUploadedUrl ?? '#';
  return (
    <a
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      className={TILE_CLASS}
    >
      {overlay}
    </a>
  );
}

const TILE_CLASS =
  'group bg-secondary border-foreground/15 relative block aspect-video w-full overflow-hidden rounded-md border text-left';

/**
 * Inline player for an uploaded clip. The browser's attribute-driven
 * `autoPlay` rejects with an uncatchable `NotAllowedError` when autoplay is
 * blocked (autoplay policy / Safari low-power), which surfaced in Sentry
 * (BALLEE-EH / BAL-355). We instead drive playback manually from an effect and
 * swallow the rejection — `controls` lets the user start it by hand. Mirrors
 * the reel player's handling in dancer-reel.tsx.
 */
function InlineUploadedVideo({ url, title }: { url: string; title: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    void videoRef.current?.play().catch(() => {
      // Autoplay blocked — the user can press play on the visible controls.
    });
  }, []);

  return (
    <video
      ref={videoRef}
      src={url}
      title={title}
      className="absolute inset-0 h-full w-full bg-black object-contain"
      controls
      playsInline
    >
      <track kind="captions" />
    </video>
  );
}

type VideoEmbed =
  | { kind: 'iframe'; url: string }
  | { kind: 'video'; url: string };

/**
 * Resolve an inline-playable embed for a clip, or null when it can only be
 * opened externally. YouTube + Vimeo → privacy-friendly iframe; uploaded
 * clips → native <video> from the signed Supabase URL.
 */
function getEmbedUrl(
  externalUrl: string | null,
  uploadedUrl: string | null,
): VideoEmbed | null {
  if (externalUrl) {
    // Single hardened resolver (host allowlist + youtube-nocookie) shared with
    // every other embed surface — see `@kit/shared/video-embed`. This tile is
    // click-to-play, so we add autoplay params to the canonical embed URL.
    const embed = resolveVideoEmbed(externalUrl);
    if (embed) {
      const url = new URL(embed.embedUrl);
      url.searchParams.set('autoplay', '1');
      if (embed.provider === 'youtube') url.searchParams.set('rel', '0');
      return { kind: 'iframe', url: url.toString() };
    }
    // Unknown / non-allowlisted host (e.g. TikTok) — caller links out.
    return null;
  }
  if (uploadedUrl) {
    return { kind: 'video', url: uploadedUrl };
  }
  return null;
}

function isHttpUrl(value: string | null | undefined): value is string {
  return (
    typeof value === 'string' &&
    (value.startsWith('http://') || value.startsWith('https://'))
  );
}

/**
 * A URL the browser can load into `<video src>`: a full http(s) URL, or a
 * same-origin absolute path (e.g. the `/api/media/cover/original/...` proxy
 * that 302s to a signed video object). A bare storage_path (no scheme, no
 * leading slash) is rejected — it raises NotSupportedError. Protocol-relative
 * `//host` is rejected too; only our own single-slash proxy paths qualify.
 */
function isPlayableUploadedUrl(
  value: string | null | undefined,
): value is string {
  if (typeof value !== 'string') return false;
  if (value.startsWith('http://') || value.startsWith('https://')) return true;
  return value.startsWith('/') && !value.startsWith('//');
}

function sourceOf(url: string | null): string | null {
  if (!url) return null;
  try {
    const host = new URL(url).hostname.replace(/^www\./, '');
    if (host.includes('youtube') || host.includes('youtu.be')) return 'YouTube';
    if (host.includes('vimeo')) return 'Vimeo';
    if (host.includes('tiktok')) return 'TikTok';
    return host;
  } catch {
    return null;
  }
}
